import { AISecurityInsights, AIComplianceInsights } from '../types';

export type { AISecurityInsights, AIComplianceInsights };

const OPENROUTER_URL = 'https://openrouter.ai/api/v1/chat/completions';

/** Ordered model fallback list. OpenRouter tries these in order and automatically
 *  routes to the next one when a model errors or is rate-limited (429), which the
 *  free pool frequently is. Override via REACT_APP_OPENROUTER_MODELS (comma-separated).
 *  Edit this list as free models come and go on OpenRouter. */
const OPENROUTER_MODELS: string[] = (
  process.env.REACT_APP_OPENROUTER_MODELS ||
  [
    'moonshotai/kimi-k2:free',
    'google/gemma-4-31b-it:free',
    'meta-llama/llama-3.3-70b-instruct:free',
    'deepseek/deepseek-chat-v3-0324:free',
    'qwen/qwen-2.5-72b-instruct:free',
  ].join(',')
)
  .split(',')
  .map((m) => m.trim())
  .filter(Boolean);

/** Free OpenRouter models can queue/stall indefinitely. Abort the request
 *  after this long so the caller's catch can fall back instead of freezing
 *  the analysis progress bar at 96%. */
const OPENROUTER_TIMEOUT_MS = 30000;

async function fetchWithTimeout(url: string, init: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), OPENROUTER_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error(`OpenRouter request timed out after ${OPENROUTER_TIMEOUT_MS / 1000}s`);
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}

interface InsightParams {
  repoName: string;
  language: string;
  topics: string[];
  overallScore: number;
  avsvLevel: 1 | 2 | 3 | null;
  secretsInCode: number;
  dependencyVulnsCount: number;
  vibeCodeRiskScore?: number;
  aiLibrariesDetected?: string[];
  vulnerabilities: Array<{
    severity: string;
    title: string;
    category: string;
    owaspCategory?: string;
  }>;
}

export async function getAISecurityInsights(params: InsightParams): Promise<AISecurityInsights> {
  const apiKey = process.env.REACT_APP_OPENROUTER_API_KEY;
  if (!apiKey) throw new Error('OpenRouter API key not configured');

  const criticalCount = params.vulnerabilities.filter(v => v.severity === 'critical').length;
  const highCount = params.vulnerabilities.filter(v => v.severity === 'high').length;
  const totalCount = params.vulnerabilities.length;

  const topFindings = params.vulnerabilities
    .filter(v => v.severity === 'critical' || v.severity === 'high')
    .slice(0, 8)
    .map(v => `- [${v.severity.toUpperCase()}] ${v.title} (${v.owaspCategory || v.category})`)
    .join('\n');

  const vibeCodeFindings = params.vulnerabilities
    .filter(v => v.category === 'vibeCode')
    .map(v => `- [${v.severity.toUpperCase()}] ${v.title}`)
    .join('\n');

  const isVibeCode = (params.aiLibrariesDetected?.length ?? 0) > 0 || vibeCodeFindings.length > 0;

  const prompt = `You are a senior application security engineer specialising in AI-generated ("vibe-coded") project security. Be concise and direct — no fluff.

Repository: ${params.repoName}
Primary Language: ${params.language || 'Unknown'}
Topics: ${params.topics.slice(0, 5).join(', ') || 'none'}
Security Score: ${params.overallScore}/100
OWASP ASVS Level: ${params.avsvLevel ? `Level ${params.avsvLevel}` : 'Not compliant — needs remediation'}
AI-Generated Project: ${isVibeCode ? `YES — uses ${params.aiLibrariesDetected?.join(', ') || 'AI libraries'}` : 'Not detected'}
AI-Code Risk Score: ${params.vibeCodeRiskScore ?? 0}/100

Findings:
- ${criticalCount} critical, ${highCount} high, ${totalCount} total vulnerabilities
- ${params.secretsInCode} secrets/credentials found in source code
- ${params.dependencyVulnsCount} vulnerable dependencies

Top Critical/High Issues:
${topFindings || '- None detected'}

${vibeCodeFindings ? `AI-Specific Risks Found:\n${vibeCodeFindings}` : ''}

Respond with ONLY a valid JSON object (no markdown, no code fences):
{
  "executiveSummary": "2-3 sentences for a non-technical founder explaining the current security risk and urgency",
  "topRisks": ["Plain-English risk 1 (max 20 words)", "Risk 2", "Risk 3"],
  "vibeCodeRisks": ["AI/vibe-code specific pattern if found, e.g. hardcoded secrets, missing auth, no input validation"],
  "remediationPlan": ["Step 1: most urgent fix", "Step 2", "Step 3", "Step 4"],
  "strengths": ["Security positive 1", "Security positive 2"]
}

Rules:
- Be specific to THIS repo's actual findings — not generic advice
- vibeCodeRisks: patterns common in AI-generated code (hardcoded secrets, eval/exec, SQLi via string concat, missing auth checks, insecure defaults). Empty array if none detected.
- Keep each string under 25 words
- Max 4 items per array`;

  const response = await fetchWithTimeout(OPENROUTER_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
      'HTTP-Referer': process.env.REACT_APP_SITE_URL || 'https://githubmate.ai',
      'X-Title': process.env.REACT_APP_SITE_NAME || 'GitHubMate',
    },
    body: JSON.stringify({
      models: OPENROUTER_MODELS,
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.2,
      max_tokens: 1500,
    }),
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    throw new Error(`OpenRouter ${response.status}: ${errText}`);
  }

  const data = await response.json();
  const content: string = data.choices?.[0]?.message?.content || '';

  // Strip markdown fences if present
  const cleaned = content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
  const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
  if (!jsonMatch) throw new Error('AI returned non-JSON response');

  const parsed = JSON.parse(jsonMatch[0]) as AISecurityInsights;

  // Ensure all required arrays exist
  return {
    executiveSummary: parsed.executiveSummary || '',
    topRisks: Array.isArray(parsed.topRisks) ? parsed.topRisks : [],
    vibeCodeRisks: Array.isArray(parsed.vibeCodeRisks) ? parsed.vibeCodeRisks : [],
    remediationPlan: Array.isArray(parsed.remediationPlan) ? parsed.remediationPlan : [],
    strengths: Array.isArray(parsed.strengths) ? parsed.strengths : [],
  };
}

interface ComplianceParams {
  repoName: string;
  language: string;
  securityScore: number;
  gdprCompliant: boolean;
  soxCompliant: boolean;
  hipaaCompliant: boolean;
  pciCompliant: boolean;
  complianceIssues: string[];
  secretsInCode: number;
  hasAuth: boolean;
  hasLogging: boolean;
  hasEncryption: boolean;
}

export async function getAIComplianceInsights(params: ComplianceParams): Promise<AIComplianceInsights> {
  const apiKey = process.env.REACT_APP_OPENROUTER_API_KEY;
  if (!apiKey) throw new Error('OpenRouter API key not configured');

  const frameworks = [
    { name: 'SOC 2', compliant: params.soxCompliant },
    { name: 'GDPR', compliant: params.gdprCompliant },
    { name: 'HIPAA', compliant: params.hipaaCompliant },
    { name: 'PCI DSS', compliant: params.pciCompliant },
  ];

  const prompt = `You are a compliance expert. Analyze this repository's compliance posture and respond with ONLY a valid JSON object (no markdown, no code fences).

Repository: ${params.repoName}
Language: ${params.language || 'Unknown'}
Security Score: ${params.securityScore}/100
Authentication: ${params.hasAuth ? 'Present' : 'Missing'}
Logging: ${params.hasLogging ? 'Present' : 'Missing'}
Encryption: ${params.hasEncryption ? 'Present' : 'Missing'}
Secrets in code: ${params.secretsInCode}
Framework compliance: ${frameworks.map(f => `${f.name}: ${f.compliant ? 'Pass' : 'Fail'}`).join(', ')}
Key issues: ${params.complianceIssues.slice(0, 6).join('; ') || 'none'}

{
  "overallAssessment": "2-sentence plain-English summary of compliance posture",
  "soc2": {
    "readiness": "Ready|Gaps Found|Not Ready",
    "topGaps": ["gap1 (max 15 words)", "gap2"],
    "quickFixes": ["fix1 (max 15 words)", "fix2"],
    "timeline": "e.g. 2-4 weeks"
  },
  "gdpr": {
    "readiness": "Ready|Gaps Found|Not Ready",
    "topGaps": ["gap1", "gap2"],
    "quickFixes": ["fix1", "fix2"],
    "timeline": "e.g. 1-2 weeks"
  },
  "hipaa": {
    "readiness": "Ready|Gaps Found|Not Ready",
    "topGaps": ["gap1", "gap2"],
    "quickFixes": ["fix1", "fix2"],
    "timeline": "e.g. 4-6 weeks"
  },
  "pci": {
    "readiness": "Ready|Gaps Found|Not Ready",
    "topGaps": ["gap1", "gap2"],
    "quickFixes": ["fix1", "fix2"],
    "timeline": "e.g. 6-8 weeks"
  },
  "priorityActions": ["Action 1 (most urgent)", "Action 2", "Action 3"],
  "estimatedEffort": "e.g. 3-6 months for full compliance"
}

Rules: Be specific to THIS repo. Max 3 items per array. Keep strings under 20 words.`;

  const response = await fetchWithTimeout(OPENROUTER_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
      'HTTP-Referer': process.env.REACT_APP_SITE_URL || 'https://githubmate.ai',
      'X-Title': process.env.REACT_APP_SITE_NAME || 'GitHubMate',
    },
    body: JSON.stringify({
      models: OPENROUTER_MODELS,
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.2,
      max_tokens: 1500,
    }),
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    throw new Error(`OpenRouter ${response.status}: ${errText}`);
  }

  const data = await response.json();
  const content: string = data.choices?.[0]?.message?.content || '';

  const cleaned = content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
  const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
  if (!jsonMatch) throw new Error('AI returned non-JSON compliance response');

  const parsed = JSON.parse(jsonMatch[0]) as AIComplianceInsights;

  const safeFramework = (f: any) => ({
    readiness: (['Ready', 'Gaps Found', 'Not Ready'].includes(f?.readiness) ? f.readiness : 'Gaps Found') as 'Ready' | 'Gaps Found' | 'Not Ready',
    topGaps: Array.isArray(f?.topGaps) ? f.topGaps : [],
    quickFixes: Array.isArray(f?.quickFixes) ? f.quickFixes : [],
    timeline: f?.timeline || 'TBD',
  });

  return {
    overallAssessment: parsed.overallAssessment || '',
    soc2: safeFramework(parsed.soc2),
    gdpr: safeFramework(parsed.gdpr),
    hipaa: safeFramework(parsed.hipaa),
    pci: safeFramework(parsed.pci),
    priorityActions: Array.isArray(parsed.priorityActions) ? parsed.priorityActions : [],
    estimatedEffort: parsed.estimatedEffort || '',
  };
}
